import { useState } from 'react';
import { Label } from '@paulownia/shared/components/ui/label';
import { CreatableSelect } from '@paulownia/shared/components/CreatableSelect';
import type { ProductOption } from '@paulownia/shared/types';
import { X, ChevronRight } from 'lucide-react';
import { OptionEditor } from '../OptionEditor';

interface WizardStepOptionsProps {
  availableOptions: ProductOption[];
  existingOptions: ProductOption[];
  onOptionsChange: (options: ProductOption[]) => void;
}

// Helper to generate camelCase ID from label
function toCamelCase(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
    .replace(/[^a-zA-Z0-9]/g, '')
    .replace(/^./, (chr) => chr.toLowerCase());
}

// Helper to get option type label
function getOptionTypeLabel(type: ProductOption['type']): string {
  switch (type) {
    case 'boolean': return 'Oui/Non';
    case 'select': return 'Choix unique';
    case 'multiselect': return 'Choix multiples';
    case 'composition': return 'Composition';
  }
}

export function WizardStepOptions({
  availableOptions,
  existingOptions,
  onOptionsChange,
}: WizardStepOptionsProps) {
  const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(null);
  const [editingOption, setEditingOption] = useState<ProductOption | null>(null);

  // Filter out composition options (handled separately) and already added options
  const filteredExistingOptions = existingOptions.filter(
    opt => opt.type !== 'composition' && !availableOptions.some(added => added.label === opt.label)
  );
  const availableOptionsToAdd = filteredExistingOptions.map(opt => opt.label);

  // Filter out composition options from display
  const displayOptions = availableOptions.filter(opt => opt.type !== 'composition');

  const startNewOption = (label: string) => {
    if (!label.trim()) return;

    const id = toCamelCase(label);
    if (availableOptions.some(opt => opt.id === id)) return;

    // Check if this option exists in other products
    const existingOption = existingOptions.find(opt => opt.label === label.trim());

    const newOption: ProductOption = existingOption
      ? {
          ...existingOption,
          id,
          choices: existingOption.choices ? [...existingOption.choices] : undefined,
        }
      : {
          id,
          label: label.trim(),
          type: 'boolean',
        };

    setEditingOption(newOption);
    setEditingOptionIndex(-1);
  };

  const startEditOption = (index: number) => {
    const opt = displayOptions[index];
    if (opt) {
      setEditingOption({ ...opt, choices: opt.choices ? [...opt.choices] : undefined });
      setEditingOptionIndex(index);
    }
  };

  const saveEditingOption = () => {
    if (!editingOption) return;

    if ((editingOption.type === 'select' || editingOption.type === 'multiselect') &&
        (!editingOption.choices || editingOption.choices.length === 0)) {
      return;
    }

    if (editingOptionIndex === -1) {
      onOptionsChange([...availableOptions, editingOption]);
    } else if (editingOptionIndex !== null) {
      // Find the real index in availableOptions (accounting for composition options)
      let realIndex = 0;
      let displayIndex = 0;
      for (let i = 0; i < availableOptions.length; i++) {
        if (availableOptions[i].type !== 'composition') {
          if (displayIndex === editingOptionIndex) {
            realIndex = i;
            break;
          }
          displayIndex++;
        }
      }
      onOptionsChange(
        availableOptions.map((opt, i) => i === realIndex ? editingOption : opt)
      );
    }

    setEditingOption(null);
    setEditingOptionIndex(null);
  };

  const cancelEditOption = () => {
    setEditingOption(null);
    setEditingOptionIndex(null);
  };

  const removeOption = (optionId: string) => {
    onOptionsChange(availableOptions.filter(opt => opt.id !== optionId));
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Options du produit</h2>
        <p className="text-sm text-gray-500 mt-1">
          Definissez les options personnalisables (optionnel).
        </p>
      </div>

      {/* Option Editor */}
      {editingOption && (
        <OptionEditor
          option={editingOption}
          isNew={editingOptionIndex === -1}
          onChange={setEditingOption}
          onSave={saveEditingOption}
          onCancel={cancelEditOption}
        />
      )}

      {/* Options list */}
      {!editingOption && (
        <>
          {displayOptions.length > 0 ? (
            <div className="space-y-2">
              {displayOptions.map((opt, index) => (
                <div
                  key={opt.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{opt.label}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">
                        {getOptionTypeLabel(opt.type)}
                      </span>
                      {opt.type === 'select' && opt.required && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-600">
                          Obligatoire
                        </span>
                      )}
                    </div>
                    {opt.choices && opt.choices.length > 0 && (
                      <div className="text-xs text-gray-500 mt-0.5 truncate">
                        {opt.choices.map(c => c.label).join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <button
                      type="button"
                      onClick={() => startEditOption(index)}
                      className="p-1.5 text-gray-400 hover:text-blue-600"
                      title="Modifier"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeOption(opt.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600"
                      title="Supprimer"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
              <p className="text-sm text-gray-500">Aucune option definie</p>
              <p className="text-xs text-gray-400 mt-1">
                Les options permettent de personnaliser le produit (ex: "sans sauce")
              </p>
            </div>
          )}

          {/* Add option */}
          <div className="pt-2">
            <Label className="text-sm text-gray-600 mb-1.5 block">Ajouter une option</Label>
            <CreatableSelect
              value=""
              onChange={(label) => {
                if (label) startNewOption(label);
              }}
              options={availableOptionsToAdd}
              placeholder="Selectionner ou creer..."
            />
          </div>
        </>
      )}
    </div>
  );
}
